import { useQuery } from "@tanstack/react-query"

import {
  getOT,
  getTraceability,
  getTraceabilityGeo,
  listOTs,
  listTraceabilityShares,
} from "@/features/transit/api/transit.api"
import type { ListOTsParams } from "@/features/transit/api/types"

export const transitKeys = {
  all: ["transit"] as const,
  list: (params: ListOTsParams) => ["transit", "list", params ?? {}] as const,
  detail: (id: string | number) => ["transit", "detail", String(id)] as const,
  traceability: (id: string | number) =>
    ["transit", "traceability", String(id)] as const,
  traceabilityGeo: (id: string | number) =>
    ["transit", "traceability-geo", String(id)] as const,
  shares: (id: string | number) => ["transit", "shares", String(id)] as const,
}

export function useTransitOrders(params: ListOTsParams = {}) {
  return useQuery({
    queryKey: transitKeys.list(params),
    queryFn: () => listOTs(params),
  })
}

export function useTransitOrder(otId: string | number | undefined) {
  return useQuery({
    queryKey: transitKeys.detail(otId ?? ""),
    queryFn: () => getOT(otId as string | number),
    enabled: !!otId,
  })
}

// Lazy traceability fetch — `enabled` keeps it idle until the consumer
// actually needs the data (viewer dialog opened or bundle export
// requested). The endpoint is heavy: it walks lots → loads → deliveries
// → producers/parcels and aggregates compliance.
export function useTransitTraceability(
  otId: string | number | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: transitKeys.traceability(otId ?? ""),
    queryFn: () => getTraceability(otId as string | number),
    enabled: !!otId && (options?.enabled ?? true),
    staleTime: 60_000,
  })
}

// Parcel geometry — fetched only when the parcels tab opens. Polygons
// can be hundreds of points each, so this stays out of the main
// traceability response.
export function useTransitTraceabilityGeo(
  otId: string | number | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: transitKeys.traceabilityGeo(otId ?? ""),
    queryFn: () => getTraceabilityGeo(otId as string | number),
    enabled: !!otId && (options?.enabled ?? true),
    staleTime: 60_000,
  })
}

// Recent shares for an OT — used by the Terminer panel's history list.
// Cheap query (one DB select), so we don't gate it.
export function useTransitShares(otId: string | number | undefined) {
  return useQuery({
    queryKey: transitKeys.shares(otId ?? ""),
    queryFn: () => listTraceabilityShares(otId as string | number),
    enabled: !!otId,
  })
}
